import React, { PureComponent, useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '@ui/card';
import Chart from 'react-apexcharts';
import styles from './index.module.sass';
import { AnalyticsMixingData } from '@@/mixing/ts/analytic.types';
import { ApexOptions } from 'apexcharts';
import ApexCharts from 'apexcharts';
import cn from 'classnames';

import { useAreaChartOptions, useLineChartOptions } from './hooks/useOptions';
import { useChartData } from './hooks';
import { getDateLabel } from './helpers/getDatalabel';
import {
  $datesLength,
  $lastPeriodSelection,
  updateDateLength,
  updateLastPeriodSelection,
} from '@/widgets/analytics/model';

export function ChartCard(props: IChartData) {
  const { data, className } = props;
  const chartRef = useRef(null);
  const [numberDays, setNumberDays] = useState<number>(0);
  const [twoDays, setTwoDays] = useState<number>(0);
  const currentPeriod = $lastPeriodSelection.getState();
  const datesLength = $datesLength.getState();

  const sortedGraph = (() => {
    const res =
      data?.graph?.sort((a, b) => {
        return +(new Date(a.created_at) > new Date(b.created_at_wt));
      }) || [];
    return res;
  })();
  const dates = sortedGraph.map((d) => new Date(d.created_at_wt).getTime());

  const categories = sortedGraph.map((d) =>
    getDateLabel(new Date(d.created_at_wt).valueOf()),
  );
  let range = 50;
  const benefit = sortedGraph.map((d) => +d.benefit);
  const state = {
    series: [
      {
        name: 'Стоимость с миксованием',
        data: sortedGraph.map((d) => +d.using_mixing),
      },
      {
        name: 'Стоимость без миксования',
        data: sortedGraph.map((d) => +d.without_mixing),
      },
      {
        name: 'Стоимость складов с учетом логистики (НЕТТО)',
        data: sortedGraph.map((d) => +d.exchange),
      },
    ],
  };
  const calculateDaysDifference = (dates: number[]) => {
    // Проверяем входные данные
    if (!Array.isArray(dates) || dates.length === 0) {
      throw new Error('Массив должен содержать хотя бы один элемент');
    }

    // Получаем первую и последнюю даты
    const firstTimestamp = dates[0];
    const lastTimestamp = dates[dates.length - 1];

    // Конвертируем разницу в милисекундах в дни
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const daysDiff = Math.floor(
      Math.abs(lastTimestamp - firstTimestamp) / millisecondsPerDay,
    );

    return daysDiff;
  };
  const findPointTwoDaysBefore = (dates: number[]): number => {
    // Проверяем входные данные
    if (!Array.isArray(dates) || dates.length === 0) {
      throw new Error('Массив должен содержать хотя бы один элемент');
    }

    const lastTimestamp = dates[dates.length - 1];

    // Вычисляем временную метку за два с половиной дня до последней даты
    const twoDaysBefore = lastTimestamp - 1000 * 60 * 60 * 24 * 2.5;

    // Находим индекс первой точки, которая старше двух дней от последней
    const index = dates.findIndex((date) => date > twoDaysBefore);

    // Если не нашли точку за два дня до последней, возвращаем последнии 14 точек
    return index !== -1 ? index : dates[Math.max(0, dates.length - 15)];
  };
  useEffect(() => {
    try {
      const days = calculateDaysDifference(dates);
      const lastTwoDays = findPointTwoDaysBefore(dates);

      setNumberDays(days);
      setTwoDays(lastTwoDays);
    } catch (error) {
      console.error('Ошибка при расчете дней:', error);
      setNumberDays(0);
      setTwoDays(0);
    }
  }, [dates]);
  useEffect(() => {
    const minCurrentPeriod = Math.max(1, Number(currentPeriod?.min));
    const maxCurrentPeriod = dates.length;
    if (currentPeriod?.max !== maxCurrentPeriod && datesLength !== maxCurrentPeriod) {
      updateLastPeriodSelection({ min: minCurrentPeriod, max: maxCurrentPeriod });
      ApexCharts.exec('chart1', 'updateOptions', {
        chart: {
          selection: {
            enabled: true,
            xaxis: {
              min: minCurrentPeriod,
              max: maxCurrentPeriod,
            },
          },
        },
      });
      updateDateLength(maxCurrentPeriod);
    }
  }, [dates]);
  const lineOptions: ApexOptions = useLineChartOptions(
    categories,
    dates,
    numberDays,
    state.series,
    benefit,
  );

  // Конфигурация второго графика
  const areaOptions: ApexOptions = useAreaChartOptions(categories, dates, twoDays);

  const series: ApexAxisChartSeries = useChartData({
    objectsArray: state.series,
    numbersArray: dates,
  });

  const handleScroll = (e: any) => {
    if (e.deltaY > 0 && range > 0) {
      range--;
    } else if (range < categories?.length) {
      range++;
    } else {
      return;
    }
    ApexCharts.exec(
      'main-chart',
      'zoomX',
      categories.length - range,
      categories.length,
    );
  };

  let benefitValues: { name: string; data: number[] } = {
    name: 'Выгода',
    data: [],
  };

  for (let i = 0; i < state.series[0].data.length; i++) {
    if (state.series[0].data[i] < 0) {
      benefitValues.data[i] = 0;
    } else {
      benefitValues.data[i] = +(
        state.series[0].data[i] - state.series[1].data[i]
      ).toFixed(4);
    }
  }
  return (
    <Card className={cn(className, styles.chart)} id="chart-container">
      <b className={cn(className, styles.chart__title)}>График цен (руб.)</b>

      {data ? (
        <>
          <Chart
            ref={chartRef}
            height={350}
            options={lineOptions}
            series={series}
            type="area"
            onWheel={handleScroll}
          />
        </>
      ) : (
        ''
      )}
      {data ? (
        <>
          <Chart
            height={250}
            options={areaOptions}
            series={series}
            type="area"
            onWheel={handleScroll}
          />
        </>
      ) : (
        ''
      )}
    </Card>
  );
}

interface IChartData {
  data: AnalyticsMixingData | null;
  className?: string;
}
